import React, {
  useEffect,
  useState,
  useRef,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  useDispatch,
  useSelector,
} from 'react-redux';
import { useSnackbar } from 'notistack';
import {
  Box,
  Container,
  Grid,
  makeStyles
} from '@material-ui/core';

import {
  clearArticlesData,
  deleteArticle,
  fetchArticles,
  setFilters,
} from 'src/store/actions/articles';
import articlesSelectors from 'src/store/selectors/articles';

import AlertDialog from 'src/components/AlertDialog';
import Page from 'src/components/Page';
import Results from '../components/Results';
import Toolbar from './Toolbar';
import Categories from '../components/Categories';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.dark,
    minHeight: '100%',
    paddingBottom: theme.spacing(3),
    paddingTop: theme.spacing(3)
  }
}));

const ArticlesListView = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();
  const [searchParams, setSearchParams] = useSearchParams();
  const deleteAlertRef = useRef();

  const [isInited, setIsInited] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const search = searchParams.get('search') || null;
  const currentPage = parseInt(searchParams.get('page') || 1, 10);
  const rowsPerPage = parseInt(searchParams.get('limit') || 10, 10);

  const isFetching = useSelector((state) => articlesSelectors.getArticlesIsFetching(state));
  const totalCount = useSelector((state) => articlesSelectors.getArticlesTotalCount(state));
  const filters = useSelector((state) => articlesSelectors.getFilters(state));

  const fetchData = async () => {
    try {
      await dispatch(fetchArticles({
        search,
        page: currentPage,
        limit: rowsPerPage,
        ...filters,
      }));
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Wystąpił problem podczas pobierania listy artykułów', { variant: 'error' });
    }
  };

  const setFiltersFromSearchParams = () => {
    // eslint-disable-next-line no-unused-vars
    const { page, limit, ...filtersArray } = Object.fromEntries(searchParams);

    dispatch(setFilters(filtersArray));
  };

  useEffect(() => {
    setIsInited(true);
    return () => {
      dispatch(clearArticlesData());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isInited) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    setFiltersFromSearchParams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleChangePage = (event, value) => {
    setSearchParams({
      page: value + 1,
      limit: rowsPerPage,
      ...filters
    });
  };

  const handleLimitChange = (event) => {
    const { value } = event.target;
    setSearchParams({
      page: currentPage,
      limit: value,
      ...filters
    });
  };

  const handleSearch = (values) => {
    Object.keys(values).forEach((key) => {
      if (values[key] === undefined) {
        delete values[key];
      }
    });

    setSearchParams({
      page: currentPage,
      limit: rowsPerPage,
      ...values
    });
  };

  const openDeleteModal = (id) => {
    deleteAlertRef.current.open();
    setSelectedId(id);
  };

  const handleDelete = async () => {
    try {
      await dispatch(deleteArticle(selectedId));
      deleteAlertRef.current.close();
      setSelectedId(null);
      enqueueSnackbar('Artykuł została usunięty', { variant: 'success' });

      await fetchData();

      return true;
    } catch (error) {
      console.log(error);
      enqueueSnackbar('Wystąpił problem podczas usuwania artykułu', { variant: 'error' });

      return error;
    }
  };

  return (
    <Page
      className={classes.root}
      title="Artykuły"
    >
      <Container maxWidth={false}>
        <Toolbar handleSearch={handleSearch} />
        <Box mt={3}>
          <Grid
            container
            spacing={3}
          >
            <Grid
              item
              md={3}
              xs={12}
            >
              <Categories handleSelectCategory={handleSearch} totalCount={totalCount} />
            </Grid>
            <Grid
              item
              md={9}
              xs={12}
            >
              <Results
                handleChangePage={handleChangePage}
                handleLimitChange={handleLimitChange}
                openDeleteModal={openDeleteModal}
              />
            </Grid>
          </Grid>
        </Box>
      </Container>
      <AlertDialog
        title="Usuwanie artykułu"
        message="Czy na pewno chcesz usunąć artykuł?"
        ref={deleteAlertRef}
        actions={[
          {
            label: 'Anuluj',
          },
          {
            autoFocus: true,
            disabled: isFetching,
            label: 'Usuń',
            loading: isFetching,
            variant: 'contained',
            onClick: handleDelete,
          },
        ]}
      />
    </Page>
  );
};

export default ArticlesListView;
